import { type IconMapping } from "@foodpilot/foods";

export type ConsentIconKey = keyof IconMapping;
export type ConsentCategoryColorKey = "error" | "primary" | "info" | "success" | "warning" | "grey";

export type ConsentCategoryConfig = {
    colorKey: ConsentCategoryColorKey;
    icon: ConsentIconKey;
    labelFallback: string;
    labelKey: string;
};

export const CONSENT_CATEGORY_CONFIG: Record<string, ConsentCategoryConfig> = {
    cgu: {
        labelKey: "settings.consent.category.terms",
        labelFallback: "Terms",
        icon: "documentSpace",
        colorKey: "error",
    },
    platform_policy: {
        labelKey: "settings.consent.category.platform_policy",
        labelFallback: "Platform policy",
        icon: "publications",
        colorKey: "info",
    },
    product_comms: {
        labelKey: "settings.consent.category.product_comms",
        labelFallback: "Product communications",
        icon: "products",
        colorKey: "warning",
    },
    institutional_comms: {
        labelKey: "settings.consent.category.institutional_comms",
        labelFallback: "Institutional communications",
        icon: "corporate",
        colorKey: "info",
    },
    data_sharing: {
        labelKey: "settings.consent.category.data_sharing",
        labelFallback: "Data sharing",
        icon: "exportDownload",
        colorKey: "success",
    },
    events: {
        labelKey: "settings.consent.category.events",
        labelFallback: "Events",
        icon: "trajectory",
        colorKey: "warning",
    },
    surveys: {
        labelKey: "settings.consent.category.surveys",
        labelFallback: "Surveys",
        icon: "questionnaires",
        colorKey: "info",
    },
};

export const DEFAULT_CONSENT_CATEGORY_CONFIG: ConsentCategoryConfig = {
    labelKey: "settings.consent.category.unknown",
    labelFallback: "—",
    icon: "documentSpace",
    colorKey: "grey",
};

export const DOCUMENT_CATEGORY_KEYS = ["cgu", "platform_policy"] as const;
export const OPT_IN_CATEGORY_KEYS = [
    "product_comms",
    "institutional_comms",
    "data_sharing",
    "events",
    "surveys",
] as const;

export type DocumentConsentCategory = (typeof DOCUMENT_CATEGORY_KEYS)[number];
export type OptInConsentCategory = (typeof OPT_IN_CATEGORY_KEYS)[number];

export const CONSENT_CREATION_CATEGORY_SPLITS = {
    document: DOCUMENT_CATEGORY_KEYS,
    opt_in: OPT_IN_CATEGORY_KEYS,
} as const;

export const CONSENT_TYPE_ICON_CONFIG = {
    document: {
        icon: "documentSpace",
    },
    opt_in: {
        icon: "communication",
    },
} as const satisfies Record<"document" | "opt_in", { icon: ConsentIconKey }>;

const normalizeCategoryAlias = (category: string) =>
    category
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const CONSENT_CATEGORY_ALIASES = Object.entries(CONSENT_CATEGORY_CONFIG).reduce<Record<string, string>>(
    (aliases, [categoryKey, categoryConfig]) => {
        aliases[normalizeCategoryAlias(categoryKey)] = categoryKey;
        aliases[normalizeCategoryAlias(categoryConfig.labelFallback)] = categoryKey;

        return aliases;
    },
    {
        politique_plateforme: "platform_policy",
    },
);

export const getConsentCategoryKey = (category?: string | null) => {
    const normalizedCategory = category?.trim();

    if (!normalizedCategory) {
        return null;
    }

    if (CONSENT_CATEGORY_CONFIG[normalizedCategory]) {
        return normalizedCategory;
    }

    return CONSENT_CATEGORY_ALIASES[normalizeCategoryAlias(normalizedCategory)] ?? null;
};

export const getConsentCategoryConfig = (category?: string | null): ConsentCategoryConfig => {
    const categoryKey = getConsentCategoryKey(category);

    if (!categoryKey) {
        return DEFAULT_CONSENT_CATEGORY_CONFIG;
    }

    return CONSENT_CATEGORY_CONFIG[categoryKey] ?? DEFAULT_CONSENT_CATEGORY_CONFIG;
};

export const hasKnownConsentCategory = (category?: string | null) => {
    return getConsentCategoryKey(category) !== null;
};
